/**
 * useXStocks — whether the xStocks section is on, the active stocks, and
 * which token symbols are xStocks.
 *
 * Several screens need the same three answers (Tokens, token detail,
 * Exchange, All assets), so results are shared through a short module-level
 * cache instead of refetching on every mount.
 *
 * When the `xstocks_enabled` app setting is off (or cannot be read) the hook
 * reports enabled=false with no stocks and no symbols — every xStock surface
 * then stays hidden, which is the safe default.
 */

import { useCallback, useEffect, useState } from 'react';
import { v2GetAppSettings, v2GetTokens, v2GetXStocks } from '../services/v2/api';
import type { V2XStock } from '../types/v2';

export interface XStocksData {
  enabled: boolean;
  /** Active stocks in admin display order. */
  stocks: V2XStock[];
  /** Exact symbols of all xStock tokens ("AMZNx"), active or not. */
  symbols: ReadonlySet<string>;
}

export interface UseXStocksResult extends XStocksData {
  loading: boolean;
  refresh: () => Promise<void>;
}

const CACHE_TTL_MS = 60_000;
const DISABLED: XStocksData = { enabled: false, stocks: [], symbols: new Set() };

let cache: { data: XStocksData; at: number } | null = null;
let inflight: Promise<XStocksData> | null = null;

/** Clears the shared cache — for tests. */
export const resetXStocksCache = () => {
  cache = null;
  inflight = null;
};

export const loadXStocks = async (force = false): Promise<XStocksData> => {
  if (!force && cache && Date.now() - cache.at < CACHE_TTL_MS) {
    return cache.data;
  }
  if (!force && inflight) {
    return inflight;
  }
  inflight = (async () => {
    try {
      const settings = await v2GetAppSettings();
      const flag = settings?.settings?.xstocks_enabled;
      if (!(flag === true || flag === 'true')) {
        return DISABLED;
      }
      const [stocksRes, tokensRes] = await Promise.all([v2GetXStocks(), v2GetTokens()]);
      return {
        enabled: true,
        stocks: stocksRes.xstocks ?? [],
        symbols: new Set(
          (tokensRes.tokens ?? []).filter(t => t.token_type === 'xstock').map(t => t.symbol),
        ),
      };
    } catch {
      // Keep whatever was known before; a first failure means "hidden".
      return cache?.data ?? DISABLED;
    }
  })();
  try {
    const data = await inflight;
    cache = { data, at: Date.now() };
    return data;
  } finally {
    inflight = null;
  }
};

export const useXStocks = (): UseXStocksResult => {
  const [data, setData] = useState<XStocksData>(cache?.data ?? DISABLED);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let active = true;
    loadXStocks().then(next => {
      if (active) {
        setData(next);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const refresh = useCallback(async () => {
    const next = await loadXStocks(true);
    setData(next);
    setLoading(false);
  }, []);

  return { ...data, loading, refresh };
};
